import {
  DHEditorSubscriberCallback,
  DHElement,
  DHUserMode
} from '../../../types/design-huddle.types'
import {
  EditTemplatePageSecondaryNavState,
  useEditTemplatePageSecondaryNavState
} from './EditTemplatePageSecondaryNav.hooks'
import { IFRAME_CONTAINER_ID } from '../DesignHuddle/constants'
import { ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { addFlowListener, removeFlowListener } from '@dtx-company/inter-app/src/redux/utils'
import { buildUpdatedTco } from './utils'
import {
  createProject,
  getProject,
  getRefreshedAccessToken,
  insertEditor
} from '../DesignHuddle/utils'
import { dhProjectChangeListener } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreMiddleware'
import { formatBatchDataToFlowcodeType } from '../BrandKit/BrandKitForm/BrandKitForm.utils'
import { getIdFromEncodedString } from '@dtx-company/true-common/src/utils/strings'
import {
  setBatchId,
  setDhProject,
  setDhProjectIdForTemplate,
  setNickname,
  setSelectedFlowcode
} from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreSlice'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useDesignHuddle } from '../DesignHuddle/hooks'
import { useDispatch } from 'react-redux'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { useGetBatchDetailsForTemplates } from '../BrandKit/BrandKitForm/BrandKitForm.hooks'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils'
import { usePrintStoreAssociateProjectMutation } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreMutations'

export const useDesignHuddleEditor = (): EditTemplatePageState => {
  const [batchIdDetailsSaved, setBatchIdDetailsSaved] = useState(false)

  const { templateId, dhTemplateId } = useCodeSelector((state: RootCodeState) => {
    return state.printStore
  })
  const dhProjectId = useDhProjectId()
  const dispatch = useDispatch()
  const batchData = useGetBatchDetailsForTemplates()
  const batchDetails = useMemo(
    () => formatBatchDataToFlowcodeType(batchData?.batch),
    [batchData?.batch]
  )

  useEffect(() => {
    if (!batchIdDetailsSaved && batchDetails) {
      const decodedBatchId = getIdFromEncodedString(batchDetails.id)
      dispatch(setSelectedFlowcode(batchDetails))
      dispatch(setBatchId(decodedBatchId))
      batchDetails.nickname && dispatch(setNickname(batchDetails.nickname))
      setBatchIdDetailsSaved(true)
    }
  }, [batchDetails, batchIdDetailsSaved, dispatch])

  const { clearCode, backButtonHandler, tco, setTco } = useEditTemplatePageSecondaryNavState()

  const {
    modalOpen: selectFlowcodeModalOpen,
    setOpen: setSelectFlowcodeModalOpen,
    setClose: setSelectFlowcodeModalClose
  } = useModal(ModalTypes.SELECT_FLOWCODE_MODAL)

  const { dhLibIsReady, DHEditorLib, dhEditorInstanceRef } = useDesignHuddle()
  const editorContainerRef = useRef<HTMLDivElement>(null)

  const [associatePrintStoreProject] = usePrintStoreAssociateProjectMutation()
  const { jwt, isAuthenticated } = useAuthState()
  const isMounted = useIsMounted()

  /**
   * If we don't already have a `projectId`, create a project in Design Huddle and associate it with the template.
   */
  useEffect(() => {
    const isClient = isMounted()
    if (
      !dhProjectId &&
      dhLibIsReady &&
      isClient &&
      DHEditorLib &&
      dhTemplateId &&
      isAuthenticated
    ) {
      createProject(dhTemplateId).then(dhProjectId => {
        dispatch(setDhProjectIdForTemplate({ dhProjectId, dhTemplateId }))
        associatePrintStoreProject({
          dhProjectId,
          sourceTemplateId: templateId,
          dhUserId: jwt?.ithacaId || 'missing_jwt',
          projectTitle: `${dhTemplateId}_${dhProjectId}`
        })
      })
    }
  }, [
    DHEditorLib,
    associatePrintStoreProject,
    dispatch,
    isMounted,
    jwt?.ithacaId,
    dhProjectId,
    dhLibIsReady,
    templateId,
    dhTemplateId,
    isAuthenticated
  ])

  /**
   * Listens to clicks in the editor. If the Flowcode element is clicked, opens the modal to select a Flowcode.
   */
  const selectionEventListener = useCallback<DHEditorSubscriberCallback<DHElement>>(
    (_editor, element) => {
      if (element && Object.keys(element).length === 1) {
        const dhElement = Object.values(element)[0]
        if (dhElement.element_classes.includes('flowcodeImage')) {
          setSelectFlowcodeModalOpen()
        }
      }
    },
    [setSelectFlowcodeModalOpen]
  )

  /**
   * Refresh access token if session times out.
   */
  const sessionExpirationEventListener = useCallback<DHEditorSubscriberCallback<void>>(
    async editor => {
      const token = await getRefreshedAccessToken(DHUserMode.GUEST)
      editor.refreshAccessToken(token)
    },
    []
  )

  /**
   * Record DH project data in Redux when it changes.
   */
  const changeEventListener = useCallback<DHEditorSubscriberCallback<unknown>>(
    async editor => {
      const projectData = await getProject(editor)
      dispatch(setDhProject(projectData))
    },
    [dispatch]
  )

  /**
   * Insert the editor iframe into the DOM when we have a `projectId`.
   *
   * Clean up by returning a function from this effect that removes the editor iframe, allowing
   * us to re-insert it upon client-side navigation back to this page. This is necessary because
   * the editor iframe is not a React component, and so it is not automatically cleaned up when
   * the component unmounts.
   */
  useEffect(() => {
    if (dhProjectId && dhLibIsReady) {
      insertEditor(IFRAME_CONTAINER_ID, dhProjectId).then(editor => {
        dhEditorInstanceRef.current = editor
        dhEditorInstanceRef.current.handleElementSelection(selectionEventListener)
        dhEditorInstanceRef.current.handleUnauthenticated(sessionExpirationEventListener)
        changeEventListener(dhEditorInstanceRef.current)
        dhEditorInstanceRef.current.handleProjectDataChange(changeEventListener)
      })
      dispatch(addFlowListener(dhProjectChangeListener))
    }
    return () => {
      if (dhEditorInstanceRef.current) {
        dhEditorInstanceRef.current.remove()
      }
      dispatch(removeFlowListener({ ...dhProjectChangeListener, cancelActive: true }))
    }
  }, [
    changeEventListener,
    dhLibIsReady,
    DHEditorLib,
    dhEditorInstanceRef,
    dhProjectId,
    selectionEventListener,
    sessionExpirationEventListener,
    dispatch
  ])

  /**
   * When the `tco` changes, update the editor with the new `tco`.
   */
  useEffect(() => {
    if (dhEditorInstanceRef.current) {
      getProject(dhEditorInstanceRef.current).then(project => {
        const updatedTco = buildUpdatedTco(project, tco)
        dhEditorInstanceRef.current?.changeElements?.(updatedTco)
      })
    }
  }, [dhEditorInstanceRef, tco])

  return {
    selectFlowcodeModalOpen,
    setSelectFlowcodeModalClose,
    clearCode,
    backButtonHandler,
    tco,
    setTco,
    editorContainerRef
  }
}

interface EditTemplatePageState extends EditTemplatePageSecondaryNavState {
  selectFlowcodeModalOpen: boolean
  setSelectFlowcodeModalClose: () => void
  editorContainerRef: React.RefObject<HTMLDivElement>
}

export const useDhProjectId = (): string | undefined => {
  const router = useFlowRouter()
  const dispatch = useDispatch()
  let dhProjectId = router.getString('dhProjectId')
  const { dhTemplateId, templateIdToDhProjectMap } = useCodeSelector((state: RootCodeState) => {
    return state.printStore
  })
  if (dhProjectId && dhTemplateId) {
    dispatch(setDhProjectIdForTemplate({ dhProjectId: dhProjectId, dhTemplateId }))
  }
  if (!dhProjectId) {
    dhProjectId = dhTemplateId && templateIdToDhProjectMap[dhTemplateId]
  }
  return dhProjectId ? dhProjectId : undefined
}
